import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/brand";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { breadcrumbLd } from "../components/Seo";

export default function AccessibilityStatement() {
  return (
    <div className="min-h-screen bg-[color:var(--dbe-cream)]">
      <Seo
        title="Accessibility Statement"
        description="Dead Bug Edwards is committed to WCAG 2.1 AA accessibility. Read our full accessibility statement, tools available on this site, and how to contact us for assistance."
        canonical="/accessibility-statement"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Accessibility Statement", path: "/accessibility-statement" },
        ])}
      />
      <Header />

      <main className="pt-[120px] pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/" className="text-sm text-[color:var(--dbe-red)] font-semibold uppercase tracking-wider hover:underline" data-testid="back-home-link">
            ← Back to Home
          </Link>

          <h1 className="mt-6 font-bebas text-5xl md:text-6xl leading-[0.95] tracking-wide">
            Website Accessibility Statement
          </h1>
          <p className="mt-3 text-sm text-neutral-500 uppercase tracking-[0.2em]">Last Updated: February 27, 2026</p>

          <Section title="Our Commitment to Accessibility">
            We are committed to ensuring digital accessibility for people with disabilities. We are continually
            improving the user experience for everyone and applying relevant accessibility standards to enhance
            usability and inclusivity. Our goal is to provide a website that is accessible to the widest
            possible audience, regardless of technology or ability.
          </Section>

          <Section title="Accessibility Standards">
            We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are
            widely recognized as the industry standard for website accessibility. Accessibility is an ongoing
            process, and we are actively working toward improving the accessibility and usability of our website.
          </Section>

          <Section title="Accessibility Technology & Tools">
            <p>
              To support our accessibility efforts, we have deployed industry-standard accessibility technology
              designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
            </p>
            <ul className="mt-3 list-disc pl-6 space-y-1">
              <li>Screen reader enhancements</li>
              <li>Keyboard navigation improvements</li>
              <li>Color contrast adjustments</li>
              <li>Text size and spacing controls</li>
              <li>Focus indicators</li>
              <li>Additional accessibility interface options</li>
            </ul>
            <p className="mt-3">
              This technology helps us improve accessibility while we continue broader auditing, remediation,
              and development efforts.
            </p>
          </Section>

          <Section title="Ongoing Improvement">
            <p>Accessibility is an ongoing initiative. We are:</p>
            <ul className="mt-3 list-disc pl-6 space-y-1">
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p className="mt-3">
              We understand accessibility is a continuous journey and are committed to making improvements over time.
            </p>
          </Section>

          <Section title="Third-Party Content">
            Some areas of the website may include third-party content, plugins, or integrations that are not
            fully controlled by us. While we encourage vendors to provide accessible content, we cannot
            guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
          </Section>

          <Section title="Feedback & Assistance">
            <p>
              If you experience difficulty accessing any part of this website, need assistance, or would like
              to request content in an alternative format, please contact us.
            </p>
            <p className="mt-3">Please include:</p>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>The page URL</li>
              <li>Description of the issue</li>
              <li>Your preferred contact method</li>
            </ul>
            <div className="mt-5 bg-white border border-black/10 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">Contact</div>
              <div className="mt-2 font-bold">{BRAND.name}</div>
              <div>{BRAND.address.line1}, {BRAND.address.city}, {BRAND.address.state} {BRAND.address.zip}</div>
              <div className="mt-1">
                <a className="text-[color:var(--dbe-red)] font-semibold" href={`tel:${BRAND.phoneTel}`}>{BRAND.phoneDisplay}</a>
                {" · "}
                <a className="text-[color:var(--dbe-red)] font-semibold" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
              </div>
            </div>
            <p className="mt-4">We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.</p>
          </Section>

          <Section title="Good Faith Efforts">
            We are making good-faith efforts to comply with accessibility standards and improve usability for
            individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest
            accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate
            your understanding as we continue improving accessibility.
          </Section>

          <Section title="Terms & Conditions - Accessibility Clause">
            We are committed to providing an accessible website experience for all users and strive to follow
            recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard
            accessibility tools and continue to review, update, and improve our website to enhance accessibility.
            Because accessibility is an ongoing process, some areas of the website may not yet fully conform to
            all accessibility standards. If you encounter accessibility barriers, please contact us so we can
            provide assistance, offer alternative access, and address the issue where feasible. Nothing in these
            Terms is intended to limit any rights available under applicable accessibility laws.
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="font-bebas text-3xl tracking-wide text-[color:var(--dbe-red)]">{title}</h2>
      <div className="mt-3 text-neutral-800 leading-relaxed text-[17px]">
        {typeof children === "string" ? <p>{children}</p> : children}
      </div>
    </section>
  );
}
